// THE COVERAGE MATRIX — the cell contract. PURE (`now` injected, no I/O).
//
// Brief: _architect/briefs/2026-09-15-coverage-page-overhaul.md D16, D19, D20
//
// Rows are ACCOUNTS, columns are SOURCES, cells are dots. This file defines what
// a cell can be and where each state comes from. `grid.ts` builds the rows.
//
// ⚠️⚠️ THIS IS A RE-PROJECTION, NOT A REBUILD. `AccountHealth` per account per
// source is ALREADY the cell — the worklist becomes the row detail and the sort
// key. Nothing about the source contracts changes.
//
// ⚠️⚠️ AND IT IS A JOIN OF TWO EXISTING HEALTH MODELS, NOT A THIRD. They answer
// different questions on the same axes and both are already built:
//
//     connectionHealth   CAN WE REACH this account's connection? (tokens)
//     sourceHealth       DID THE DATA LAND? (delivery, activity, coverage depth)
//
// Introducing a third model to hold "the matrix view" would be the same mistake
// as `causeJobKey` and `freshnessMeaningful`: a new object whose existence is
// evidence the existing ones were not read.

import type { HealthStatus } from './status.js';

/**
 * The four columns that SHIP.
 *
 * ⚠️⚠️ GA4 IS ABSENT, NOT PENDING. A column of all "–" would say "not enabled for
 * this account", when the truth is "we do not collect this at all" — which is a
 * statement about US, not about the account. There is no GA4 export, no dataset,
 * no query layer (D1). It joins when there is data to report.
 *
 * ⚠️ Comments and posts join ONE SOURCE AT A TIME as each is wired, for the same
 * reason: a column exists once it can distinguish its own states.
 */
export const MATRIX_SOURCES = ['meta', 'metaDemographics', 'googleAds', 'crm'] as const;
export type MatrixSourceKey = (typeof MATRIX_SOURCES)[number];

/**
 * ⚠️⚠️ "Meta Ads" AND "Meta Ads Demo", NOT "Meta" (D22a5). The reason is
 * FORWARD-LOOKING and worth honouring exactly: organic Meta columns (comments,
 * posts) are coming, and these two must ALREADY read as the AD ACCOUNT ones
 * before they arrive. Renaming afterwards means every operator who learned
 * "Meta" as the ad column has to unlearn it at the moment a second Meta column
 * appears beside it — which is precisely when the distinction starts to matter.
 */
export const MATRIX_SOURCE_LABEL: Record<MatrixSourceKey, string> = {
  meta: 'Meta Ads',
  metaDemographics: 'Meta Ads Demo',
  googleAds: 'Google Ads',
  crm: 'CRM',
};

/**
 * What one cell can be.
 *
 * ⚠️⚠️ `notEnabled` IS READ FROM ACCOUNT CONFIG AND NEVER INFERRED FROM ABSENCE OF
 * DATA. "This source isn't enabled for this account" and "this source is broken
 * for this account" both produce no rows, and rendering them the same makes a
 * deliberate configuration look like a fault — or, worse, a fault look
 * deliberate. A matrix cannot omit a cell the way a worklist can omit a row, so
 * the state has to exist and has to have a source.
 *
 * ⚠️⚠️ `unknown` AND `missing` ARE TWO STATES. They arrive through the same hole
 * and mean opposite things:
 *
 *     unknown   our health query for the source FAILED. We know nothing about
 *               ANY account. Genuine uncertainty, and it says so.
 *     missing   the source is enabled and the pull does NOT reach this account.
 *               A DISCREPANCY: signed up, never wired.
 *
 * ⚠️ `missing` IS NOT UNCERTAINTY — config and pipeline disagree about whether
 * this account exists, which is exactly the finding this page was built to
 * surface. Calling it `unknown` buries a configuration error inside a shrug.
 *
 * ⚠️⚠️ ITS COLOUR IS STILL NOT DECIDED, AND THE COUNT IT WAITS ON IS NOT ONE WE
 * HOLD — see `grid.ts`'s note on the discrepancy count. It renders as its own
 * flagged-but-uncoloured state, is counted, and has its own filter preset. Do
 * not paint it red until the live number is measured.
 *
 * ⚠️⚠️ `stillSyncing` AND `decommissioned` EXIST SO THE CANCELLED INVERSION
 * CANNOT BE GOT BACKWARDS (D20a). On a cancelled account the question the cell
 * answers CHANGES — see `matrixCell`.
 *
 * ⚠️⚠️ `drift` IS A CONFIG FAULT, NOT A DATA FAULT, AND THAT IS WHY IT IS ITS
 * OWN KIND RATHER THAN A RED `health` (D22a1). The data may be landing
 * PERFECTLY — from the WRONG AD ACCOUNT. That is worse than no data, because
 * nothing about the numbers looks wrong: a franchise location quietly reporting
 * another location's spend passes every freshness check there is.
 *
 * ⚠️ So it must never be outranked by a staleness amber. Modelling it as a
 * `health` verdict would put it in the same ordering as staleness and leave the
 * precedence to whichever branch happened to run first; as its own kind, checked
 * BEFORE the verdict, being outranked is not expressible. It carries BOTH ad
 * account ids because "drifted" without saying from what to what is not
 * actionable.
 */
export type MatrixCell =
  | { kind: 'notEnabled'; reason: string }
  | { kind: 'unknown'; reason: string }
  | { kind: 'missing'; reason: string }
  | { kind: 'health'; status: HealthStatus; reason: string | null }
  | { kind: 'stillSyncing'; reason: string }
  | { kind: 'decommissioned'; reason: string }
  | {
      kind: 'drift';
      reason: string;
      /** What kyck has bound. Normalised `act_…`, or null when unset. */
      boundAdAccountId: string | null;
      /** What the Zernio profile is actually scoped to. */
      expectedAdAccountIds: string[];
    };

/**
 * The ACCOUNT CONFIG a cell is read from. Deliberately a narrow projection
 * rather than the whole `Account`.
 *
 * ⚠️⚠️ THERE IS NO `runsMeta` / `runsGoogle` HERE, AND THAT IS THE D19a FIX MADE
 * STRUCTURAL. Those fields are a DERIVED MIRROR of `services`, not independent
 * evidence of anything:
 *
 *     platformEnabled.ts:38    "on save, runsMeta = 'meta_ads' IN services"
 *     channelVisibility.ts:11  "services is the truth BY DESIGN since Phase 2a;
 *                               runsGoogle is a derived mirror kept for
 *                               backwards compatibility"
 *
 * The census measured PERFECT agreement in both directions and read it as a
 * discovery; it was a tautology, written down in this repo before the question
 * was asked. `services` is also the switch Scott actually operates, so it is
 * what the word "not enabled" means to the person reading the page. A field that
 * is absent cannot be read by mistake.
 */
export interface MatrixAccountConfig {
  accountId: string;
  accountName: string;
  /** ⚠️ 'active' | 'paused' | 'cancelled'. Flips what a cell MEANS — see below. */
  accountStatus: string;
  /** The ticked services. THE enablement witness. */
  services: readonly string[];
  /** `Account.crmId` — the club link. CRM has no service to tick, so this is its switch. */
  crmId: string | null;
}

/**
 * ⚠️⚠️ CRM'S SWITCH IS THE LINK BECAUSE CRM HAS NO SERVICE, NOT AS AN EXCEPTION
 * TO D19a. There is no `crm` entry in the service vocabulary
 * (`src/lib/accountServices.ts`), so `crmId` is the only deliberate
 * configuration act that says "this account has a CRM". Reading a link where a
 * service exists is the D19b error; reading a link where no service exists is
 * reading the only switch there is.
 */
export const SERVICE_FOR_SOURCE: Record<MatrixSourceKey, string | null> = {
  meta: 'meta_ads',
  // ⚠️ DEMOGRAPHICS INHERITS META'S SERVICE because it is a sub-pull of the same
  // runnable set — the nightly job runs over the accounts the refresh runs over.
  // It gets its own COLUMN because its coverage is separately measured and
  // separately broken, but not its own switch, which would invent a control
  // nobody can operate.
  metaDemographics: 'meta_ads',
  googleAds: 'google_ads',
  crm: null,
};

/** Is this source switched on for this account, per CONFIG alone? */
export function enabledFor(
  source: MatrixSourceKey,
  config: MatrixAccountConfig,
): { enabled: true } | { enabled: false; reason: string } {
  const service = SERVICE_FOR_SOURCE[source];
  if (service === null) {
    return config.crmId !== null && config.crmId.trim() !== ''
      ? { enabled: true }
      : { enabled: false, reason: 'This account is not linked to a CRM club.' };
  }
  if (config.services.includes(service)) return { enabled: true };
  return {
    enabled: false,
    reason:
      source === 'googleAds'
        ? 'Google Ads is not one of this account’s services.'
        : 'Meta Ads is not one of this account’s services.',
  };
}

export const isCancelled = (config: MatrixAccountConfig): boolean =>
  config.accountStatus === 'cancelled';

/**
 * Build one cell.
 *
 * ⚠️ ENABLEMENT IS CHECKED FIRST AND SHORT-CIRCUITS. A disabled source has no
 * health to report, and asking for one would produce a verdict about a pipeline
 * that is not meant to run.
 *
 * ⚠️⚠️ THEN CANCELLED, BEFORE ANY VERDICT IS CONSULTED — AND THAT ORDER IS THE
 * WHOLE OF D20a. On a cancelled account the SUBJECT OF THE FINDING CHANGES:
 *
 *     ACTIVE     stale data = the fault.      live sync = correct.
 *     CANCELLED  stale data = EXPECTED.       LIVE SYNC = THE FAULT.
 *
 * So a cancelled row's cell answers "is this source still WIRED?", not "is the
 * data healthy?" — which is why it can never be `health`. The branch is placed
 * ABOVE the verdict rather than beside it so the staleness path is UNREACHABLE
 * for a cancelled account: painting one amber for being stale is not discouraged
 * here, it is not expressible. An account that stopped advertising because the
 * CLIENT NO LONGER EXISTS is the least faulty thing on the page, and that is the
 * dormancy error (D9c, D10d) in its purest form.
 *
 * ⚠️ `paused` is NOT cancelled and is deliberately treated as active. A paused
 * client is expected back; the sync running is correct, and staleness is still
 * the fault. Only `cancelled` flips.
 *
 * ⚠️⚠️ `sourceFailed` OUTRANKS the health we happen to hold. A source whose query
 * failed may still have a cached or partial `AccountHealth`, and rendering that
 * as fact would present stale data as current — the same inversion as a page
 * that silently drops a failed source and shows a shorter worklist.
 */
export function matrixCell(args: {
  source: MatrixSourceKey;
  config: MatrixAccountConfig;
  sourceFailed: boolean;
  /**
   * ⚠️ DOES THE PULL ACTUALLY REACH THIS ACCOUNT — by its own link OR by shared
   * coverage. From the code that already decides (`accountSelection.ts` for
   * Meta, the attribution directory for Google, the club registry for CRM),
   * never re-derived here. D19c: when a rule governs behaviour that is already
   * implemented somewhere, that implementation is the contract.
   */
  inRoster: boolean;
  /** The account's verdict for this source, or null when the source has no row for it. */
  verdict: { status: HealthStatus; reason: string | null } | null;
  /**
   * ⚠️ ACTIONABLE ad-account drift, ALREADY CLASSIFIED by
   * `src/lib/adAccountDrift.ts` and gated by its own `isActionableDrift` — the
   * SAME pair `connectionHealth/roster.ts` uses, so the grid and the Accounts
   * page cannot disagree about what counts as drift. Null when there is none.
   * Only the Meta columns can carry it; there is no Google or CRM equivalent.
   */
  drift?: {
    kind: string;
    boundAdAccountId: string | null;
    expectedAdAccountIds: string[];
  } | null;
}): MatrixCell {
  const { source, config, sourceFailed, inRoster, verdict, drift = null } = args;

  const enabled = enabledFor(source, config);
  if (!enabled.enabled) return { kind: 'notEnabled', reason: enabled.reason };

  if (isCancelled(config)) {
    return inRoster
      ? {
          kind: 'stillSyncing',
          reason: 'This client has cancelled and we are still pulling this source.',
        }
      : {
          kind: 'decommissioned',
          reason: 'Cancelled, and this source no longer reaches the account.',
        };
  }

  // ⚠️⚠️ DRIFT OUTRANKS EVERY DATA VERDICT, AND IS CHECKED BEFORE THEM SO THAT
  // IS STRUCTURAL. An account bound to the wrong ad account can be perfectly
  // fresh — the staleness checks below would return GREEN and the cell would
  // say everything is fine while the numbers belong to somebody else.
  //
  // ⚠️ It sits BELOW the cancelled branch deliberately: a cancelled account's
  // wiring being wrong is not a finding worth chasing, and the decommission
  // task is the same either way.
  if (drift !== null) {
    return {
      kind: 'drift',
      reason: driftReason(drift),
      boundAdAccountId: drift.boundAdAccountId,
      expectedAdAccountIds: drift.expectedAdAccountIds,
    };
  }

  if (sourceFailed) {
    return { kind: 'unknown', reason: 'This source could not be checked.' };
  }

  // ⚠️⚠️ ENABLED, CHECKED, AND THE PULL DOES NOT REACH IT. Not green, not "–",
  // and NOT `unknown` — the query succeeded, so this is not uncertainty. It is a
  // DISCREPANCY: the service is ticked and the pipeline has no route to this
  // account. Signed up, never wired.
  if (!inRoster) {
    return {
      kind: 'missing',
      reason: 'Switched on for this account, but nothing is wired up to pull it.',
    };
  }

  // ⚠️ In the roster and still no verdict: the pull reaches it and the source
  // reported nothing for it. Same discrepancy from the other side, and folding it
  // into a green would be the silent failure the page exists to catch.
  if (verdict === null) {
    return {
      kind: 'missing',
      reason: 'The pull covers this account, but this source has no record of it.',
    };
  }

  return { kind: 'health', status: verdict.status, reason: verdict.reason };
}

/**
 * The colour a cell renders as, or null for the states that deliberately have
 * none.
 *
 * ⚠️⚠️ `missing` RETURNS null ON PURPOSE. Its count has not been measured (see
 * `grid.ts`), and D19b's own correction to the 154 figure is itself wrong — so
 * colouring it now would be the third guess at the same number. It renders as a
 * distinct flagged state that is counted and filterable, which surfaces the
 * finding without asserting a severity nobody has established.
 *
 * ⚠️⚠️ `stillSyncing` RETURNS null TOO, AND FOR A DIFFERENT REASON: a
 * decommission backlog is not an outage and must never outrank one (D20b#4). It
 * gets its own view, its own preset and its own marker — not a red dot competing
 * with a dead pull job.
 */
export function cellStatus(cell: MatrixCell): HealthStatus | null {
  // ⚠️⚠️ DRIFT IS RED. It is the one non-`health` state with a colour, because
  // unlike `missing` (count unmeasured) and `stillSyncing` (a backlog, not an
  // outage) its severity is not in doubt: we are reporting one client's spend
  // under another client's name.
  if (cell.kind === 'drift') return 'red';
  return cell.kind === 'health' ? cell.status : null;
}

/**
 * ⚠️ NAMES BOTH SIDES. "Ad-account drift" alone is a label, not a finding —
 * whoever reads it has to open two other screens to learn which account is
 * bound and which is expected, and that is the work the page exists to remove.
 */
function driftReason(d: {
  kind: string;
  boundAdAccountId: string | null;
  expectedAdAccountIds: string[];
}): string {
  const expected = d.expectedAdAccountIds.join(', ');
  if (d.kind === 'unset') {
    return `No ad account is set on this account, but its Meta profile is scoped to ${expected}.`;
  }
  if (d.kind === 'profile-disconnected') {
    return `This account is bound to ${d.boundAdAccountId ?? 'an ad account'}, but its Meta profile is scoped to nothing — the connection has dropped.`;
  }
  return `This account is bound to ${d.boundAdAccountId ?? 'an ad account'}, but its Meta profile is scoped to ${expected}. Data may be landing from the wrong ad account.`;
}
